/* eslint-disable react/prop-types */
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  memo,
} from "react";
import {
  IonModal,
  IonToolbar,
  IonTitle,
  IonButton,
  IonContent,
  IonText,
  IonInput,
  IonIcon,
  IonSpinner,
  IonSkeletonText,
  IonFooter,
} from "@ionic/react";
import { heart } from "ionicons/icons";
import "./CommentReplies.css";
import useAuth from "../../../hooks/useAuth";
import {
  fetchReplies,
  deleteComment,
  editComment,
} from "../../../services/postsService";
import { PostData } from "../../../types/socialJournal";
import { formatTimeAgoShort } from "../../../utils/dateHelpers";
import { wrapText, resolveProfileDisplay } from "../../../utils/helpers";
import Avatar from "../../ui/Avatar";
import { Comment } from "../../../services/postsService";
import CommentActionDialog from "./CommentActionDialog";
// Extend your existing Comment interface for UI state
interface ExtendedComment extends Comment {
  showReplies?: boolean;
  userHasLiked?: boolean;
  user_reaction?: string | null;
  repliesLoading?: boolean;
  repliesPage?: number;
  repliesHasMore?: boolean;
}

interface ReactionResult {
  action: "added" | "removed";
  reactions?: { [emoji: string]: number };
}

interface PaginationData {
  currentPage: number;
  totalPages: number;
  total: number;
  limit: number;
  hasMore?: boolean;
  nextCursor?: number | null;
}

interface LoadMoreResult {
  comments: Comment[];
  pagination: PaginationData;
}

interface ReplyModalProps {
  isOpen: boolean;
  post: PostData;
  onClose: () => void;
  onSendReply: (content: string, parentCommentId?: number) => Promise<Comment>;
  onReact: (replyId: number, emoji: string) => Promise<ReactionResult | void>;
  loadMoreComments: (page: number) => Promise<LoadMoreResult>; // Top-level comments
  // fetchReplies: (commentId: number, postId: number) => Promise<Comment[]>; // Fetch child replies
  initialReplies?: Comment[];
  totalComments?: number;
  autoFocus?: boolean;
}

// ============================================
// MEMOIZED COMMENT COMPONENT
// ============================================

interface CommentItemProps {
  comment: ExtendedComment;
  isNested: boolean;
  onReactionToggle: (commentId: number, emoji: string) => void;
  onLoadReplies: (commentId: number) => void;
  onToggleReplies: (commentId: number) => void;
  setReplyingTo: (replyTo: { id: number; name: string }) => void;
  canDelete: boolean;
  onDelete: (commentId: number) => void;
  currentUserId: number | null;
  deletingId: number | null;
  activeDeleteId: number | null;
  onOpenDelete: (commentId: number) => void;
  onCloseDelete: () => void;
}

const CommentItem = memo<CommentItemProps>(
  ({
    comment,
    isNested,
    onReactionToggle,
    onLoadReplies,
    onToggleReplies,
    setReplyingTo,
    canDelete,
    onDelete,
    currentUserId,
    deletingId,
    activeDeleteId,
    onOpenDelete,
    onCloseDelete,
  }) => {
    const authorProfile = resolveProfileDisplay({
      firstName: comment.author?.first_name,
      lastName: comment.author?.last_name,
      email: comment.author?.email,
      phone: comment.author?.phone,
      avatar: comment.author?.avatar,
    });
    const name = authorProfile.name;
    const heartCount = comment.reactions?.["❤️"] || 0;
    const isLiked = comment.userHasLiked || false;
    const commentId = Number(comment.comment_id);
    const hasReplies = (comment.reply_count || 0) > 0;
    const showReplies = comment.showReplies || false;
    const repliesLoading = comment.repliesLoading || false;
    const replies = comment.replies || [];
    const isDeleting = deletingId === commentId;
    const deleteOpen = canDelete && activeDeleteId === commentId;

    const wrappedText = useMemo(
      () => wrapText(comment.content || "", isNested ? 30 : 33),
      [comment.content, isNested],
    );
    const handleReplyToComment = useCallback(
      (commentId: number, name: string): void => {
        setReplyingTo({ id: commentId, name });
        // inputRef.current?.setFocus();
      },
      [],
    );

    // Long-press detection for delete
    const longPressTimerRef = useRef<any>(null);
    const LONG_PRESS_MS = 500;

    // Cleanup timer on unmount
    useEffect(() => {
      return () => {
        if (longPressTimerRef.current) {
          clearTimeout(longPressTimerRef.current);
        }
      };
    }, []);

    const startLongPress = useCallback(() => {
      if (!canDelete || isDeleting) return;
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = setTimeout(() => {
        onOpenDelete(commentId);
      }, LONG_PRESS_MS);
    }, [canDelete, isDeleting, onOpenDelete, commentId]);

    const cancelLongPress = useCallback(() => {
      if (longPressTimerRef.current) {
        clearTimeout(longPressTimerRef.current);
        longPressTimerRef.current = null;
      }
    }, []);
    const handleDeleteClick = useCallback(
      (e: React.MouseEvent | React.TouchEvent) => {
        e.stopPropagation();
        if (!isDeleting) onDelete(commentId);
        onCloseDelete();
      },
      [onDelete, commentId, isDeleting, onCloseDelete],
    );
    return (
      <div
        className={`reply-item ${isNested ? "nested-reply" : ""}`}
        data-comment-id={commentId}
      >
        <div className="reply-content-wrapper flex gap-3">
          <Avatar
            initials={authorProfile.initials}
            imageSrc={authorProfile.avatar || undefined}
            imageClassName="rounded-[12px] border"
            innerClassName="avatar-inner-38"
          />

          <div className="flex-1 min-w-0">
            <div
              className="reply-bubble"
              style={{ position: "relative" }}
              onMouseDown={startLongPress}
              onMouseUp={cancelLongPress}
              onMouseLeave={cancelLongPress}
              onTouchStart={startLongPress}
              onTouchEnd={cancelLongPress}
              onTouchMove={cancelLongPress}
            >
              <div className="reply-header flex items-center gap-2">
                <span
                  className="reply-author font-medium text-gray-900 text-[15px] truncate block"
                  style={{ maxWidth: "150px" }}
                  title={name}
                >
                  {name}
                </span>
                <span className="text-xs text-gray-500">
                  {formatTimeAgoShort(new Date(comment.created_at))}
                </span>
              </div>

              {canDelete && deleteOpen && (
                <button
                  className="delete-comment-btn"
                  style={{
                    position: "absolute",
                    top: "0px",
                    right: "30px",
                    background:
                      "linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)",
                    border: "1px solid #FCA5A5",
                    color: "#DC2626",
                    borderRadius: "20px",
                    padding: "6px 16px",
                    fontSize: "14px",
                    fontWeight: "600",
                    boxShadow:
                      "0 4px 12px rgba(220, 38, 38, 0.15), 0 2px 4px rgba(0,0,0,0.1)",
                    zIndex: 2,
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    transform: deleteOpen ? "scale(1)" : "scale(0.95)",
                    opacity: deleteOpen ? "1" : "0",
                  }}
                  onClick={handleDeleteClick}
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <span className="flex items-center gap-2">
                      <IonSpinner
                        name="dots"
                        style={{ width: "16px", height: "16px" }}
                      />
                      <span>Deleting...</span>
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M2 4h12M5.333 4V2.667a1.333 1.333 0 011.334-1.334h2.666a1.333 1.333 0 011.334 1.334V4m2 0v9.333a1.333 1.333 0 01-1.334 1.334H4.667a1.333 1.333 0 01-1.334-1.334V4h9.334z"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span>Delete</span>
                    </span>
                  )}
                </button>
              )}

              <div className="flex items-center justify-between">
                <div className="flex items-start justify-between w-full relative">
                  <p className="reply-text text-sm text-gray-700 leading-relaxed break-words whitespace-pre-wrap flex-1">
                    {wrappedText.map((line, index, arr) => (
                      <React.Fragment key={index}>
                        {line}
                        {index < arr.length - 1 && <br />}
                      </React.Fragment>
                    ))}
                  </p>

                  <div
                    className="flex flex-col items-center gap-0 absolute top-0"
                    style={{ right: "10px" }}
                  >
                    <button
                      className="heart-btn flex items-center justify-center transition-transform duration-200 active:scale-110"
                      onClick={() => onReactionToggle(commentId, "❤️")}
                      style={{ marginTop: "2px" }}
                      aria-label={isLiked ? "Unlike comment" : "Like comment"}
                    >
                      {isLiked ? (
                        <IonIcon
                          icon={heart}
                          className="heart-icon-filled"
                          style={{
                            color: "#FF0000",
                            fontSize: "18px",
                            transition: "all 0.2s ease",
                          }}
                        />
                      ) : (
                        <svg
                          width="16"
                          height="14"
                          viewBox="0 0 16 14"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          style={{ transition: "all 0.2s ease" }}
                        >
                          <path
                            d="M10.125 0C8.83438 0 7.70438 0.555 7 1.49312C6.29562 0.555 5.16563 0 3.875 0C2.84764 0.00115796 1.86269 0.409788 1.13624 1.13624C0.409788 1.86269 0.00115796 2.84764 0 3.875C0 8.25 6.48688 11.7913 6.76312 11.9375C6.83594 11.9767 6.91732 11.9972 7 11.9972C7.08268 11.9972 7.16406 11.9767 7.23687 11.9375C7.51312 11.7913 14 8.25 14 3.875C13.9988 2.84764 13.5902 1.86269 12.8638 1.13624C12.1373 0.409788 11.1524 0.00115796 10.125 0ZM7 10.925C5.85875 10.26 1 7.23063 1 3.875C1.00099 3.11281 1.30421 2.38212 1.84316 1.84316C2.38212 1.30421 3.11281 1.00099 3.875 1C5.09062 1 6.11125 1.6475 6.5375 2.6875C6.57517 2.77921 6.63925 2.85764 6.7216 2.91284C6.80396 2.96804 6.90086 2.99752 7 2.99752C7.09914 2.99752 7.19604 2.96804 7.2784 2.91284C7.36075 2.85764 7.42483 2.77921 7.4625 2.6875C7.88875 1.64562 8.90938 1 10.125 1C10.8872 1.00099 11.6179 1.30421 12.1568 1.84316C12.6958 2.38212 12.999 3.11281 13 3.875C13 7.22563 8.14 10.2594 7 10.925Z"
                            fill="#6D7980"
                          />
                        </svg>
                      )}
                    </button>

                    {heartCount > 0 && (
                      <IonText
                        className="text-xs text-gray-500"
                        style={{
                          fontSize: "11px",
                          lineHeight: "1",
                          marginTop: "2px",
                        }}
                      >
                        {heartCount}
                      </IonText>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Show/Hide Replies Button */}
            <div className="reply-actions-row flex gap-3 my-2">
              <button
                className="action-link text-xs text-gray-600 font-medium transition-all duration-200"
                onClick={() => handleReplyToComment(commentId, name)}
              >
                Comment
              </button>
              {hasReplies && !isNested && (
                <button
                  className="toggle-replies-btn text-xs text-blue-600 font-medium"
                  onClick={() => {
                    if (!showReplies && replies.length === 0) {
                      // First time loading replies
                      onLoadReplies(commentId);
                    } else {
                      // Toggle visibility
                      onToggleReplies(commentId);
                    }
                  }}
                  disabled={repliesLoading}
                >
                  {repliesLoading ? (
                    <IonSpinner
                      name="dots"
                      style={{ width: "14px", height: "14px" }}
                    />
                  ) : (
                    <>
                      {showReplies ? "Hide" : "Show"} {comment.reply_count}{" "}
                      {comment.reply_count === 1 ? "reply" : "replies"}
                    </>
                  )}
                </button>
              )}
            </div>

            {/* Nested Replies */}
            {showReplies && replies.length > 0 && !isNested && (
              <div className="nested-replies">
                {replies.map((reply) => (
                  <CommentItem
                    key={reply.comment_id}
                    comment={reply}
                    isNested={true}
                    onReactionToggle={onReactionToggle}
                    onLoadReplies={onLoadReplies}
                    onToggleReplies={onToggleReplies}
                    setReplyingTo={setReplyingTo}
                    canDelete={
                      currentUserId != null &&
                      Number(
                        (reply as any).author?.id || (reply as any).author_id,
                      ) === currentUserId
                    }
                    onDelete={onDelete}
                    currentUserId={currentUserId}
                    deletingId={deletingId}
                    activeDeleteId={activeDeleteId}
                    onOpenDelete={onOpenDelete}
                    onCloseDelete={onCloseDelete}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.comment.comment_id === nextProps.comment.comment_id &&
      prevProps.comment.userHasLiked === nextProps.comment.userHasLiked &&
      prevProps.comment.content === nextProps.comment.content &&
      prevProps.comment.reactions?.["❤️"] ===
        nextProps.comment.reactions?.["❤️"] &&
      prevProps.comment.showReplies === nextProps.comment.showReplies &&
      prevProps.comment.repliesLoading === nextProps.comment.repliesLoading &&
      prevProps.comment.replies === nextProps.comment.replies &&
      prevProps.comment.replies?.length === nextProps.comment.replies?.length &&
      prevProps.comment.reply_count === nextProps.comment.reply_count &&
      prevProps.canDelete === nextProps.canDelete &&
      prevProps.deletingId === nextProps.deletingId &&
      prevProps.activeDeleteId === nextProps.activeDeleteId
    );
  },
);

CommentItem.displayName = "CommentItem";

// ============================================
// MAIN COMPONENT
// ============================================

const CommentReplyModal: React.FC<ReplyModalProps> = ({
  isOpen,
  post,
  onClose,
  onSendReply,
  onReact,
  loadMoreComments,
  initialReplies = [],
  totalComments = 0,
  autoFocus,
}) => {
  const { user } = useAuth();
  const [comments, setComments] = useState<ExtendedComment[]>([]);
  const [replyText, setReplyText] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [activeDeleteId, setActiveDeleteId] = useState<number | null>(null);
  const ignoreNextClickRef = useRef<boolean>(false);
  const [confirmDeleteFor, setConfirmDeleteFor] =
    useState<ExtendedComment | null>(null);
  const [replyingTo, setReplyingTo] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const contentRef = useRef<HTMLIonContentElement>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  const pendingReactionsRef = useRef<Set<number>>(new Set());
  const suppressLoadMoreRef = useRef<boolean>(false);
  const hasLoadedRef = useRef<boolean>(false);
  const inputRef = useRef<HTMLIonInputElement | null>(null);

  const emojiReactions = useMemo<string[]>(
    () => ["😔", "😊", "😍", "🤩", "❤️", "😁", "😅", "😂"],
    [],
  );

  const findCommentInTree = useCallback(
    (list: ExtendedComment[], id: number): ExtendedComment | undefined => {
      for (const c of list) {
        if (Number(c.comment_id) === id) return c;
        if (c.replies && c.replies.length > 0) {
          const found = findCommentInTree(c.replies as ExtendedComment[], id);
          if (found) return found;
        }
      }
      return undefined;
    },
    [],
  );

  const handleOpenDelete = useCallback(
    (id: number) => {
      // Open confirmation overlay with full comment details
      const found = findCommentInTree(comments, id);
      if (found) {
        setConfirmDeleteFor(found);
      }
      // Do not set activeDeleteId to avoid inline button; use overlay instead
      // Prevent the immediate click (mouseup -> click) after long-press from closing it
      ignoreNextClickRef.current = true;
    },
    [comments, findCommentInTree],
  );

  const handleCloseDelete = useCallback(() => {
    setActiveDeleteId(null);
  }, []);

  const findRootParent = useCallback(
    (targetId: number): number | null => {
      const containsId = (
        list: ExtendedComment[] | undefined,
        id: number,
      ): boolean => {
        if (!list || list.length === 0) return false;
        for (const item of list) {
          if (Number(item.comment_id) === id) return true;
          if (containsId(item.replies as ExtendedComment[] | undefined, id))
            return true;
        }
        return false;
      };

      for (const c of comments) {
        if (Number(c.comment_id) === targetId) return Number(c.comment_id);
        if (containsId(c.replies as ExtendedComment[] | undefined, targetId)) {
          return Number(c.comment_id);
        }
      }
      return null;
    },
    [comments],
  );

  const handleSetReplyingTo = useCallback(
    (replyTo: { id: number; name: string }) => {
      setReplyingTo(replyTo);
      setTimeout(() => inputRef.current?.setFocus(), 50);
    },
    [],
  );

  // ============================================
  // HELPER FUNCTIONS
  // ============================================

  const normalizeReactionKeys = useCallback((input?: { [k: string]: any }) => {
    const out: { [key: string]: number } = {};
    if (!input) return out;
    let heartCount = 0;
    for (const key of Object.keys(input)) {
      const raw = (input as any)[key];
      const val = typeof raw === "number" ? raw : Number(raw) || 0;
      const k = (key || "").toString();
      const lower = k.toLowerCase();
      const isHeartKey =
        k === "❤️" ||
        k === "❤" ||
        lower.includes("heart") ||
        lower.includes("love") ||
        lower.includes("like");
      if (isHeartKey) {
        heartCount += val;
      } else {
        out[k] = Math.max(0, val | 0);
      }
    }
    if (heartCount > 0) {
      out["❤️"] = heartCount;
    } else if (out["❤️"]) {
      out["❤️"] = Math.max(0, out["❤️"] | 0);
    }
    return out;
  }, []);

  const processComment = useCallback((comment: Comment): ExtendedComment => {
    const hasLiked =
      (comment as any).userHasLiked === true ||
      (comment as any).user_reaction === "❤️";

    const normalizedReactions = normalizeReactionKeys(
      (comment as any).reactions,
    );

    return {
      ...comment,
      comment_id: comment.comment_id,
      post_id: comment.post_id || comment.post,
      author_id: comment.author_id,
      content: comment.content,
      created_at: comment.created_at,
      updated_at: comment.updated_at,
      parent_comment_id: comment.parent_comment_id || comment.parentComment,
      userHasLiked: hasLiked,
      reactions: normalizedReactions,
      reply_count: (comment as any).reply_count || 0,
      replies: [],
      repliesLoading: false,
      repliesPage: 1,
      repliesHasMore: false,
      showReplies: false,
      author: comment.author,
    } as ExtendedComment;
  }, []);

  const updateCommentReaction = useCallback(
    (
      comments: ExtendedComment[],
      targetId: number,
      emoji: string,
      isLiked: boolean,
      newReactions?: { [emoji: string]: number },
      nextUserHasLiked?: boolean,
    ): ExtendedComment[] => {
      let changed = false;
      const next = comments.map((comment) => {
        const commentId = Number(comment.comment_id);

        if (commentId === targetId) {
          changed = true;
          if (newReactions) {
            const cloned = normalizeReactionKeys(newReactions);
            return {
              ...comment,
              userHasLiked:
                typeof nextUserHasLiked === "boolean"
                  ? nextUserHasLiked
                  : comment.userHasLiked,
              reactions: cloned,
            };
          }
          const currentCount = comment.reactions?.[emoji] || 0;
          const newCount = isLiked
            ? Math.max(0, currentCount - 1)
            : currentCount + 1;
          return {
            ...comment,
            userHasLiked: !isLiked,
            reactions: { ...(comment.reactions || {}), [emoji]: newCount },
          };
        }

        let updated = comment;
        if (comment.replies && comment.replies.length > 0) {
          const updatedReplies = updateCommentReaction(
            comment.replies,
            targetId,
            emoji,
            isLiked,
            newReactions,
            nextUserHasLiked,
          );
          if (updatedReplies !== comment.replies) {
            changed = true;
            updated = { ...comment, replies: updatedReplies };
          }
        }
        return updated;
      });
      return changed ? next : comments;
    },
    [normalizeReactionKeys],
  );

  // ============================================
  // LOAD CHILD REPLIES (LAZY LOADING)
  // ============================================

  const handleLoadReplies = useCallback(
    async (commentId: number): Promise<void> => {
      // Set loading state
      setComments((prev) =>
        prev.map((comment) =>
          Number(comment.comment_id) === commentId
            ? { ...comment, repliesLoading: true }
            : comment,
        ),
      );

      try {
        const replies = await fetchReplies(commentId, Number(post.id));
        const processedReplies = replies.map(processComment);

        setComments((prev) =>
          prev.map((comment) =>
            Number(comment.comment_id) === commentId
              ? {
                  ...comment,
                  replies: processedReplies,
                  repliesLoading: false,
                  showReplies: true,
                  repliesPage: 1,
                  repliesHasMore: false, // No pagination for now
                }
              : comment,
          ),
        );
      } catch (error) {
        console.error("Failed to load replies:", error);
        setComments((prev) =>
          prev.map((comment) =>
            Number(comment.comment_id) === commentId
              ? { ...comment, repliesLoading: false }
              : comment,
          ),
        );
      }
    },
    [fetchReplies, post.id, processComment],
  );

  const handleToggleReplies = useCallback((commentId: number): void => {
    setComments((prev) =>
      prev.map((comment) =>
        Number(comment.comment_id) === commentId
          ? { ...comment, showReplies: !comment.showReplies }
          : comment,
      ),
    );
  }, []);

  // ============================================
  // LOAD MORE TOP-LEVEL COMMENTS
  // ============================================
  const handleLoadMore = useCallback(async (): Promise<void> => {
    if (!hasMore || loadingMore) return;

    setLoadingMore(true);

    try {
      const nextPage = currentPage + 1;
      const result = await loadMoreComments(nextPage);

      if (result && result.comments && result.comments.length > 0) {
        const processedComments = result.comments.map(processComment);

        setComments((prev) => [...prev, ...processedComments]);
        setCurrentPage(nextPage);

        const hasMoreComments = result.pagination?.hasMore || false;
        setHasMore(hasMoreComments);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Failed to load more comments:", error);
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, currentPage, loadMoreComments, processComment]);

  // ============================================
  // INFINITE SCROLL FOR TOP-LEVEL COMMENTS
  // ============================================

  useEffect(() => {
    if (!hasMore || loadingMore || isSending) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMore &&
          !loadingMore &&
          !suppressLoadMoreRef.current &&
          !isSending
        ) {
          handleLoadMore();
        }
      },
      { threshold: 0.5, rootMargin: "100px" },
    );

    const el = loadMoreTriggerRef.current;
    if (el) observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, loadingMore, isSending, handleLoadMore]);

  // ============================================
  // LOAD INITIAL COMMENTS
  // ============================================

  const loadInitialComments = useCallback(async (): Promise<void> => {
    setIsLoading(true);

    try {
      const result = await loadMoreComments(1);

      if (result && result.comments) {
        const processedComments = result.comments.map(processComment);
        setComments(processedComments);
        setCurrentPage(1);

        const hasMoreComments = result.pagination?.hasMore || false;
        setHasMore(hasMoreComments);
      } else {
        setHasMore(false);
        setComments([]);
      }
    } catch (error) {
      console.error("Failed to load initial comments:", error);
      setHasMore(false);
      setComments([]);
    } finally {
      setIsLoading(false);
    }
  }, [loadMoreComments, processComment]);

  // ============================================
  // EFFECTS
  // ============================================

  useEffect(() => {
    if (!isOpen) {
      setComments([]);
      setCurrentPage(1);
      setHasMore(false);
      setIsLoading(false);
      setReplyText("");
      hasLoadedRef.current = false;
      setActiveDeleteId(null);
      return;
    }
    if (hasLoadedRef.current) return;
    setCurrentPage(1);
    setReplyText("");
    hasLoadedRef.current = true;
    loadInitialComments();
  }, [isOpen, post.id, loadInitialComments]);

  // Global click/touch listener to close any open delete button when clicking elsewhere
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: Event) => {
      // Ignore the very first click after long-press opens the delete
      if (e.type === "click" && ignoreNextClickRef.current) {
        ignoreNextClickRef.current = false;
        return;
      }
      const target = e.target as HTMLElement | null;
      if (target && target.closest && target.closest(".delete-comment-btn")) {
        return; // keep open if interacting with the delete button itself
      }
      if (activeDeleteId !== null) {
        setActiveDeleteId(null);
      }
    };
    document.addEventListener("click", handler, true);
    document.addEventListener("touchstart", handler, true);
    return () => {
      document.removeEventListener("click", handler, true);
      document.removeEventListener("touchstart", handler, true);
    };
  }, [isOpen, activeDeleteId]);

  const reset = useCallback(() => {
    setComments([]);
    setCurrentPage(1);
    setHasMore(false);
    setIsLoading(false);
    setReplyText("");
    hasLoadedRef.current = false;
  }, []);

  // useEffect(() => {
  //   if (autoFocus && isOpen) {
  //     setTimeout(() => inputRef.current?.setFocus(), 300);
  //   }
  // }, [autoFocus, isOpen]);

  // ============================================
  // EVENT HANDLERS
  // ============================================

  const handleSendReply = useCallback(async (): Promise<void> => {
    if (!replyText.trim() || isSending) return;

    suppressLoadMoreRef.current = true;
    setIsSending(true);
    const tempReplyText = replyText;
    const parentId = replyingTo?.id
      ? findRootParent(replyingTo.id) ?? replyingTo.id
      : undefined;
    const tempId = Date.now();

    try {
      const tempComment: ExtendedComment = {
        comment_id: tempId,
        post_id: Number(post.id) || 0,
        post: Number(post.id) || 0,
        author_id: user?.profile_id || 0,
        content: tempReplyText,
        created_at: new Date().toISOString(),
        reactions: {},
        userHasLiked: false,
        reply_count: 0,
        repliesLoading: false,
        repliesPage: 1,
        repliesHasMore: false,
        showReplies: false,
        author: {
          id: user?.profile_id || 0,
          user_id: user?.id || 0,
          first_name: user?.first_name || "",
          last_name: user?.last_name || "",
          username: user?.username || "",
          avatar: user?.avatar || null,
        },
        replies: [],
      };

      if (parentId) {
        // Optimistically add temp child reply to the correct parent in comments
        setComments((prev) =>
          prev.map((c) => {
            if (Number(c.comment_id) === parentId) {
              return {
                ...c,
                showReplies: true,
                reply_count: (c.reply_count || 0) + 1,
                replies: [...(c.replies || []), tempComment],
              };
            }
            return c;
          }),
        );
      } else {
        // Optimistically add temp top-level comment
        setComments((prev) => [...prev, tempComment]);
      }

      setReplyText("");
      setReplyingTo(null);

      const newComment = await onSendReply(tempReplyText, parentId);
      const processed = processComment(newComment);

      if (parentId) {
        setComments((prev) =>
          prev.map((c) => {
            if (Number(c.comment_id) === parentId) {
              return {
                ...c,
                replies: (c.replies || []).map((r) =>
                  Number(r.comment_id) === tempId ? processed : r,
                ),
              };
            }
            return c;
          }),
        );
      } else {
        setComments((prev) =>
          prev.map((c) => (Number(c.comment_id) === tempId ? processed : c)),
        );
      }

      suppressLoadMoreRef.current = true;
      setTimeout(() => {
        contentRef.current?.scrollToBottom(300);
      }, 100);
      setTimeout(() => {
        suppressLoadMoreRef.current = false;
      }, 600);
    } catch (error) {
      console.error("Failed to send reply:", error);
      setReplyText(tempReplyText);
      // Rollback optimistic changes on failure
      if (parentId) {
        setComments((prev) =>
          prev.map((c) => {
            if (Number(c.comment_id) === parentId) {
              return {
                ...c,
                reply_count: Math.max(0, (c.reply_count || 0) - 1),
                replies: (c.replies || []).filter(
                  (r) => Number(r.comment_id) !== tempId,
                ),
              };
            }
            return c;
          }),
        );
      } else {
        setComments((prev) =>
          prev.filter((c) => Number(c.comment_id) !== tempId),
        );
      }
    } finally {
      setIsSending(false);
      if (suppressLoadMoreRef.current) {
        setTimeout(() => {
          suppressLoadMoreRef.current = false;
        }, 600);
      }
    }
  }, [
    replyText,
    isSending,
    post.id,
    user,
    onSendReply,
    processComment,
    findRootParent,
  ]);

  const handleEmojiSelect = useCallback((emoji: string): void => {
    setReplyText((prev) => prev + emoji);
    // inputRef.current?.setFocus();
  }, []);

  const handleReactionToggle = useCallback(
    async (commentId: number, emoji: string): Promise<void> => {
      if (pendingReactionsRef.current.has(commentId)) {
        return;
      }

      // Optimistic update using the freshest state
      let initialLiked = false;
      setComments((prev) => {
        const target = findCommentInTree(prev, commentId);
        initialLiked = target?.userHasLiked || false;
        return updateCommentReaction(prev, commentId, emoji, initialLiked);
      });

      try {
        pendingReactionsRef.current.add(commentId);
        const result = await onReact(commentId, emoji);

        if (result && result.reactions) {
          const normalized = normalizeReactionKeys(result.reactions);
          const nextUserHasLiked =
            (result as any).action === "added"
              ? true
              : (result as any).action === "removed"
              ? false
              : undefined;

          setComments((prev) => {
            const target = findCommentInTree(prev, commentId);
            const currentHeart = target?.reactions?.[emoji] || 0;
            const serverHeart = normalized[emoji] || 0;
            // If server is stale (e.g., doesn't include our optimistic delta), skip applying server map
            const optimisticDelta = initialLiked ? -1 : 1;
            const expected = Math.max(0, currentHeart); // current already contains optimistic
            const serverMatchesDirection =
              (optimisticDelta > 0 && serverHeart >= expected) ||
              (optimisticDelta < 0 && serverHeart <= expected);

            if (serverMatchesDirection) {
              return updateCommentReaction(
                prev,
                commentId,
                emoji,
                initialLiked,
                normalized,
                nextUserHasLiked,
              );
            }
            // Keep optimistic state to avoid flicker
            return prev;
          });
        }
      } catch (error) {
        console.error("Failed to react:", error);
        // Rollback with freshest state (toggle back)
        setComments((prev) => {
          const afterOptimistic = findCommentInTree(prev, commentId);
          const isLikedNow = afterOptimistic?.userHasLiked || false;
          return updateCommentReaction(prev, commentId, emoji, isLikedNow);
        });
      } finally {
        pendingReactionsRef.current.delete(commentId);
      }
    },
    [findCommentInTree, onReact, updateCommentReaction, normalizeReactionKeys],
  );

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  // ============================================
  // DELETE COMMENT HANDLER
  // ============================================
  const removeCommentFromTree = useCallback(
    (
      list: ExtendedComment[],
      targetId: number,
      parentId: number | null = null,
    ): {
      next: ExtendedComment[];
      removed: ExtendedComment | null;
      parentId: number | null;
      index: number;
    } => {
      for (let i = 0; i < list.length; i++) {
        const c = list[i];
        const cId = Number(c.comment_id);
        if (cId === targetId) {
          const removed = c;
          const next = [...list.slice(0, i), ...list.slice(i + 1)];
          return { next, removed, parentId, index: i };
        }
        if (c.replies && c.replies.length > 0) {
          const res = removeCommentFromTree(
            c.replies as ExtendedComment[],
            targetId,
            Number(c.comment_id),
          );
          if (res.removed) {
            const updatedParent: ExtendedComment = {
              ...c,
              replies: res.next,
              reply_count: Math.max(0, (c.reply_count || 0) - 1),
            };
            const next = [...list];
            next[i] = updatedParent;
            return {
              next,
              removed: res.removed,
              parentId: res.parentId,
              index: res.index,
            };
          }
        }
      }
      return { next: list, removed: null, parentId, index: -1 };
    },
    [],
  );

  const insertCommentBack = useCallback(
    (
      list: ExtendedComment[],
      parentId: number | null,
      index: number,
      commentToInsert: ExtendedComment,
    ): ExtendedComment[] => {
      if (parentId == null) {
        const arr = [...list];
        const pos = Math.max(0, Math.min(index, arr.length));
        arr.splice(pos, 0, commentToInsert);
        return arr;
      }
      return list.map((c) => {
        if (Number(c.comment_id) === parentId) {
          const replies = [...(c.replies || [])];
          const pos = Math.max(0, Math.min(index, replies.length));
          replies.splice(pos, 0, commentToInsert);
          return {
            ...c,
            replies,
            reply_count: (c.reply_count || 0) + 1,
            showReplies: true,
          };
        }
        if (c.replies && c.replies.length > 0) {
          return {
            ...c,
            replies: insertCommentBack(
              c.replies as ExtendedComment[],
              parentId,
              index,
              commentToInsert,
            ),
          };
        }
        return c;
      });
    },
    [],
  );

  const handleDeleteComment = useCallback(
    async (targetId: number): Promise<void> => {
      if (!targetId || deletingId) return;

      // Ensure user owns this comment
      const target = findCommentInTree(comments, targetId);
      const isOwn =
        Number(target?.author?.id || target?.author_id) ===
        Number(user?.profile_id);
      if (!isOwn) return;

      setDeletingId(targetId);

      // Optimistic removal
      const { next, removed, parentId, index } = removeCommentFromTree(
        comments,
        targetId,
      );
      if (!removed) {
        setDeletingId(null);
        return;
      }
      setComments(next);

      try {
        await deleteComment(targetId);
        setDeletingId(null);
      } catch (err) {
        console.error("Failed to delete comment:", err);
        // Rollback
        setComments((prev) =>
          insertCommentBack(prev, parentId, index, removed),
        );
        setDeletingId(null);
      }
      // Close overlay if open
      setConfirmDeleteFor(null);
    },
    [
      comments,
      user?.profile_id,
      deletingId,
      findCommentInTree,
      removeCommentFromTree,
      insertCommentBack,
    ],
  );

  const handleEditComment = useCallback(
    async (targetId: number, newContent: string): Promise<void> => {
      try {
        const updated = await editComment(targetId, newContent);
        const applyUpdate = (
          list: ExtendedComment[],
          id: number,
          content: string,
          updatedAt?: string,
        ): ExtendedComment[] => {
          let changed = false;
          const next = list.map((c) => {
            if (Number(c.comment_id) === id) {
              changed = true;
              return { ...c, content, updated_at: updatedAt || c.updated_at };
            }
            if (c.replies && c.replies.length > 0) {
              const updatedReplies = applyUpdate(
                c.replies as ExtendedComment[],
                id,
                content,
                updatedAt,
              );
              if (updatedReplies !== c.replies) {
                changed = true;
                return { ...c, replies: updatedReplies };
              }
            }
            return c;
          });
          return changed ? next : list;
        };

        setComments((prev: ExtendedComment[]) =>
          applyUpdate(
            prev,
            targetId,
            updated.content,
            (updated as any).updated_at,
          ),
        );
        setConfirmDeleteFor(null);
      } catch (err) {
        console.error("Failed to edit comment:", err);
        // Consider adding user feedback here, e.g.:
        // showToast("Failed to save edit. Please try again.");
      }
    },
    [],
  );

  const sortedComments = useMemo(
    () =>
      [...comments].sort((a, b) => {
        const dateA = new Date(a.updated_at || a.created_at).getTime();
        const dateB = new Date(b.updated_at || b.created_at).getTime();
        return dateB - dateA; // Latest first
      }),
    [comments],
  );

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={handleClose}
      breakpoints={[0, 1]}
      initialBreakpoint={1}
      className="comments-replies-modal"
    >
      <IonToolbar className="flex items-center justify-center">
        <IonTitle className="text-center">Comments</IonTitle>
      </IonToolbar>

      <IonContent
        className="ion-padding-horizontal ion-padding-top"
        ref={contentRef}
        scrollY={true}
        style={{
          "--padding-bottom": "0px",
        }}
      >
        <div
          className="overflow-y-auto pt-2"
          style={{
            height: "100%",
            background: "#fff",
          }}
        >
          {isLoading ? (
            <div className="skeleton-container space-y-4 py-4 px-4">
              {[1, 2, 3].map((n) => (
                <div key={n} className="flex gap-3 items-start">
                  <IonSkeletonText
                    animated
                    style={{
                      width: "38px",
                      height: "38px",
                      borderRadius: "12px",
                      flexShrink: 0,
                    }}
                  />
                  <div className="flex-1">
                    <IonSkeletonText
                      animated
                      style={{
                        width: "50%",
                        height: "12px",
                        marginBottom: "6px",
                      }}
                    />
                    <IonSkeletonText
                      animated
                      style={{
                        width: "80%",
                        height: "10px",
                        marginBottom: "4px",
                      }}
                    />
                    <IonSkeletonText
                      animated
                      style={{ width: "60%", height: "10px" }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              {sortedComments.map((comment) => (
                  <CommentItem
                    key={comment.comment_id}
                    comment={comment}
                    isNested={false}
                    onReactionToggle={handleReactionToggle}
                    onLoadReplies={handleLoadReplies}
                    onToggleReplies={handleToggleReplies}
                    setReplyingTo={handleSetReplyingTo}
                    canDelete={
                      user?.profile_id != null &&
                      Number(
                        (comment as any).author?.id || (comment as any).author_id,
                      ) === Number(user.profile_id)
                    }
                    onDelete={handleDeleteComment}
                    currentUserId={Number(user?.profile_id || 0)}
                    deletingId={deletingId}
                    activeDeleteId={activeDeleteId}
                    onOpenDelete={handleOpenDelete}
                    onCloseDelete={handleCloseDelete}
                  />
                ))}

              {/* Infinite scroll trigger */}
              {hasMore && !isSending && (
                <div
                  ref={loadMoreTriggerRef}
                  className="flex justify-center py-4"
                >
                  {loadingMore && <IonSpinner name="dots" color="primary" />}
                </div>
              )}

              {comments.length === 0 && !isLoading && (
                <div className="empty-state flex flex-col items-center justify-center py-10 px-4">
                  <IonText className="text-gray-600 text-lg font-medium">
                    No comments yet.
                  </IonText>
                  <IonText className="text-gray-400 text-sm mt-2">
                    Be the first to comment!
                  </IonText>
                </div>
              )}
            </>
          )}
        </div>
      </IonContent>

      <IonFooter>
        <IonToolbar className="comment-replies-input-toolbar">
          <div className="emoji-bar px-4 py-3 overflow-x-auto">
            <div className="emoji-list flex gap-2">
              {emojiReactions.map((emoji, index) => (
                <button
                  key={index}
                  className="emoji-btn"
                  onClick={() => handleEmojiSelect(emoji)}
                >
                  {emoji}
                </button>
              ))}
            </div>
          </div>

          <div className="reply-input-section px-4">
            <div className="flex items-center gap-2">
              <Avatar
                initials={
                  resolveProfileDisplay({
                    firstName: user?.first_name,
                    lastName: user?.last_name,
                    email: user?.email,
                    phone: user?.phone,
                  }).initials
                }
                imageSrc={user?.avatar}
                imageClassName="rounded-[12px] border"
                innerClassName="avatar-inner-38"
              />

              <div className="input-wrapper-custom flex-1">
                <IonInput
                  ref={inputRef}
                  value={replyText}
                  placeholder={
                    replyingTo
                      ? `Comment to ${replyingTo.name}...`
                      : "Add your comment here"
                  }
                  onIonInput={(e) => setReplyText(e.detail.value || "")}
                  className="reply-input-field"
                  enterkeyhint="send"
                  onKeyDown={(e: any) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSendReply();
                    }
                  }}
                />
              </div>

              <IonButton
                fill="solid"
                onClick={handleSendReply}
                disabled={!replyText.trim() || isSending}
                className="post-btn-custom flex-shrink-0"
                shape="round"
                size="small"
              >
                Send
              </IonButton>
            </div>
          </div>
        </IonToolbar>
      </IonFooter>

      <CommentActionDialog
        isOpen={!!confirmDeleteFor}
        comment={confirmDeleteFor}
        isDeleting={
          !!confirmDeleteFor &&
          deletingId === Number(confirmDeleteFor.comment_id)
        }
        onCancel={() => setConfirmDeleteFor(null)}
        onConfirm={(id) => handleDeleteComment(id)}
        onEdit={(id, content) => handleEditComment(id, content)}
      />
    </IonModal>
  );
};

export default CommentReplyModal;
